import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONSTANTS } from '../../data';
import { AudioManager } from '../../lib/AudioManager';
import SettingsModal from './SettingsModal';

const PauseMenu = ({ onContinue, onMainMenu, audioManager }: { onContinue: () => void; onMainMenu: () => void; audioManager: AudioManager }) => {
  const [showSettings, setShowSettings] = useState(false);

  const handleContinue = () => {
    audioManager.playSound('buttonClick');
    onContinue();
  };

  const handleMainMenu = () => {
    audioManager.playSound('buttonClick');
    onMainMenu();
  };

  const handleSettingsClick = () => {
    audioManager.playSound('buttonClick');
    setShowSettings(true);
  };

  const handleSettingsClose = () => {
    audioManager.playSound('buttonClick');
    setShowSettings(false);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[4500] flex flex-col items-center justify-center backdrop-blur-md"
      style={{ background: 'var(--color-primary)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="flex flex-col items-center gap-8 p-10 rounded-3xl shadow-2xl border-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary-dark), var(--color-primary))`,
          borderColor: 'var(--color-accent)'
        }}
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <motion.h1
          className="text-5xl font-extrabold drop-shadow-lg tracking-wide text-center"
          style={{ color: 'var(--color-accent)' }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 120, damping: 10 }}
        >
          {CONSTANTS.UI_TEXT.PAUSE_TITLE}
        </motion.h1>
        
        <motion.div 
          className="mt-4 flex flex-col gap-4 w-full max-w-xs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <motion.button
            onClick={handleContinue}
            className="w-full px-10 py-4 rounded-xl font-extrabold text-2xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: 'var(--color-button)',
              color: 'var(--color-button-text)',
              borderColor: 'var(--color-accent)'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CONSTANTS.UI_TEXT.CONTINUE_BUTTON}
          </motion.button>
          <motion.button
            onClick={handleSettingsClick}
            className="w-full px-10 py-4 rounded-xl font-extrabold text-xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(to right, var(--color-primary-dark), var(--color-primary))`,
              color: 'var(--color-text)',
              borderColor: 'var(--color-accent)'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CONSTANTS.UI_TEXT.SETTINGS_BUTTON}
          </motion.button>
          <motion.button
            onClick={handleMainMenu}
            className="w-full px-10 py-4 rounded-xl font-extrabold text-xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: 'var(--color-secondary)',
              color: 'var(--color-text)',
              borderColor: 'var(--color-accent)'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CONSTANTS.UI_TEXT.MAIN_MENU_BUTTON}
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <SettingsModal onClose={handleSettingsClose} audioManager={audioManager} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PauseMenu;
